import React, { useState, useEffect, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import {
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Checkbox
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import EnhancedTableHead from "./EnhancedTableHead";
import EnhancedTableToolbar from "./EnhancedTableToolbar";
import FilterSection from "./FilterSection";
import TileView from "./TileView";
import Pagination from "./Pagination";
import { get, isEmpty } from "lodash";
import useApi from "../hooks/useApi";

/**
 * Compares two objects based on a specific key in descending order.
 * @param {object} a - First object.
 * @param {object} b - Second object.
 * @param {string} orderBy - The key to compare.
 * @returns {number} Comparison result.
 */
function descendingComparator(a, b, orderBy) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

/**
 * Returns a comparator function based on the order and orderBy.
 * @param {string} order - 'asc' or 'desc'.
 * @param {string} orderBy - The key to sort by.
 * @returns {function} Comparator function.
 */
function getComparator(order, orderBy) {
	return order === "desc"
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

/**
 * Sorts an array stably based on a comparator.
 * @param {Array} array - Array of items.
 * @param {function} comparator - Comparator function.
 * @returns {Array} Sorted array.
 */
function stableSort(array, comparator) {
	const stabilizedThis = array.map((el, index) => [el, index]);
	stabilizedThis.sort((a, b) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) return order;
		return a[1] - b[1];
	});
	return stabilizedThis.map((el) => el[0]);
}

const DEFAULT_ORDER = "asc";
const DEFAULT_ORDER_BY = ""; // Adjust as per your data
const DEFAULT_ROWS_PER_PAGE = 5;

/**
 * Retrieves records from the server.
 * @param {Object} baseApiCall - The base api call function from useApi.
 * @param {Object} values - The query parameters.
 * @param {number} values.page - The current page number.
 * @param {number} values.rowsPerPage - Number of rows per page.
 * @param {Object} values.filter - Filter values.
 * @param {Object} values.searchAdditionalQueryParams - Additional query parameters.
 * @param {string} values.searchEndpoint - The server endpoint to fetch records from.
 * @param {string} [values.order] - The sort order ('asc' or 'desc').
 * @param {string} [values.orderBy] - The column to sort by.
 * @returns {Promise<Object>} Object containing loading state, rows and totalCount.
 */
const getRecords = async (baseApiCall, values) => {
	const { page, rowsPerPage, filter, searchAdditionalQueryParams, searchEndpoint, order, orderBy } = values;
	const filteredParams = Object.keys(filter).reduce((acc, key) => {
		if (filter[key] !== "") acc[key] = filter[key];
		return acc;
	}, {});

	const queryParams = {
		pageOffset: page,
		pageSize: rowsPerPage,
		...filteredParams,
		...searchAdditionalQueryParams
	};

	// Include sorting parameters if available
	if (orderBy && order) {
		queryParams.sortField = orderBy;
		queryParams.sortDirection = order;
	}

	const queryString = Object.keys(queryParams).length
		? "?" + new URLSearchParams(queryParams).toString()
		: "";
	const endpoint = `${searchEndpoint}${queryString}`;

	const response = await baseApiCall(endpoint);
	return {
		loading: false,
		rows: response.rows,
		totalCount: response.totalCount
	};
};

/**
 * EnhancedTable Component.
 *
 * Renders a table with sorting, pagination, filtering, and CSV download capabilities.
 *
 * @component
 * @param {object} props - The component props.
 * @param {string} props.tableName - Name of the table, used for the CSV file download.
 * @param {string} [props.searchEndpoint] - Endpoint URL to fetch table data.
 * @param {object} [props.searchAdditionalQueryParams] - Additional query parameters for fetching data.
 * @param {Function} props.actionRowClick - Callback function invoked when a row is clicked.
 * @param {Function} props.actionDelete - Callback function invoked to delete selected rows.
 * @param {Array} [props.filterList] - List of filter configurations for the table.
 * @param {Array} props.headCells - Array defining the table header cells with metadata.
 * @param {number} [props.pageSize] - Number of rows to display per page.
 * @param {Array} [props.rows] - Initial rows to display in the table.
 * @param {Function} [props.refreshFunction] - Optional external function to refresh table data.
 * @param {React.Ref} ref - Ref object to allow external access to internal methods.
 * @returns {JSX.Element} The rendered EnhancedTable component.
 */
export const EnhancedTable = React.forwardRef((props, ref) => {
	const { rows: initialRows, refreshFunction } = props;
	const [order, setOrder] = useState(DEFAULT_ORDER);
	const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
	const [shouldRefresh, setShouldRefresh] = useState(false);
	const { baseApiCall } = useApi();

	const [state, setState] = useState({
		headCells: props.headCells,
		rows: initialRows || [],
		page: 0,
		totalCount: initialRows ? initialRows.length : 0,
		rowsPerPage: props.pageSize || DEFAULT_ROWS_PER_PAGE,
		visibleRows: initialRows
			? stableSort(initialRows, getComparator(DEFAULT_ORDER, DEFAULT_ORDER_BY)).slice(0, DEFAULT_ROWS_PER_PAGE)
			: [],
		searchEndpoint: props.searchEndpoint,
		dense: false,
		showFilter: false,
		paddingHeight: 0,
		searchAdditionalQueryParams: props.searchAdditionalQueryParams || {},
		selectedRows: [],
		filter: {}
	});

	const theme = useTheme();
	const isTileView = useMediaQuery(theme.breakpoints.down("md"));

	useEffect(() => {
		if (props.filterList) {
			const filterValues = props.filterList.reduce((acc, filter) => {
				acc[filter.id] = filter.defaultValue || "";
				return acc;
			}, {});
			setState((prev) => ({ ...prev, filter: filterValues }));
		}
	}, [props.filterList]);

	useEffect(() => {
		if (!props.searchEndpoint) {
			setState((prev) => ({
				...prev,
				rows: initialRows,
				totalCount: initialRows ? initialRows.length : 0,
				visibleRows: initialRows
					? stableSort(initialRows, getComparator(order, orderBy)).slice(
							prev.page * prev.rowsPerPage,
							prev.page * prev.rowsPerPage + prev.rowsPerPage
					  )
					: []
			}));
		}
	}, [initialRows, order, orderBy]);

	useEffect(() => {
		if (props.searchEndpoint) {
			/**
			 * Fetches data from the server and updates the component state.
			 */
			const fetchData = async () => {
				try {
					const result = await getRecords(baseApiCall, {
						page: state.page,
						rowsPerPage: state.rowsPerPage,
						filter: state.filter,
						searchAdditionalQueryParams: state.searchAdditionalQueryParams,
						searchEndpoint: state.searchEndpoint,
						order: order,
						orderBy: orderBy
					});

					const numEmptyRows = Math.max(0, state.rowsPerPage - result.rows.length);
					const newPaddingHeight = (state.dense ? 24 : 42) * numEmptyRows;

					setState((prev) => ({
						...prev,
						...result,
						visibleRows: result.rows,
						paddingHeight: newPaddingHeight
					}));
				} catch (e) {
					console.error(e);
				}
			};

			fetchData();
		}
	}, [
		state.page,
		state.rowsPerPage,
		state.filter,
		state.searchAdditionalQueryParams,
		order,
		orderBy,
		shouldRefresh,
	]);

	useImperativeHandle(ref, () => ({
		/**
		 * Externally callable refresh method.
		 * Calls the provided refreshFunction or resets internal state.
		 */
		onRefresh: () => {
			if (refreshFunction) {
				refreshFunction(); // Call external refresh function
			} else {
				setState((prev) => ({
					...prev,
					page: 0,
					rows: [],
					totalCount: 0,
					visibleRows: []
				}));
			}
		}
	}));

	/**
	 * Handles the sorting logic when a table header cell is clicked.
	 * @param {object} event - The click event.
	 * @param {string} newOrderBy - The id of the column to sort by.
	 */
	const handleRequestSort = (event, newOrderBy) => {
		const isAsc = orderBy === newOrderBy && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(newOrderBy);
		// Reset to first page to reflect new sort order
		setState((prev) => ({ ...prev, page: 0 }));
	};

	/**
	 * Handles the event when the 'select all' checkbox is clicked.
	 * @param {object} event - The change event.
	 */
	const handleSelectAllClick = (event) => {
		setState((prev) => ({
			...prev,
			selectedRows: event.target.checked ? prev.rows.map((n) => n._id) : []
		}));
	};

	/**
	 * Invokes the actionRowClick callback when a row is clicked.
	 * @param {object} event - The click event.
	 * @param {object} row - Data for the clicked row.
	 */
	const handleClick = (event, row) => {
		props.actionRowClick({ index: state.selectedRows.indexOf(row._id), rowData: row });
	};

	/**
	 * Toggles the selection of a row.
	 * @param {object} event - The click event.
	 * @param {object} row - Data for the row to toggle.
	 */
	const handleCheck = (event, row) => {
		const selectedIndex = state.selectedRows.indexOf(row._id);
		let newSelected = [];

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(state.selectedRows, row._id);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(state.selectedRows.slice(1));
		} else if (selectedIndex === state.selectedRows.length - 1) {
			newSelected = newSelected.concat(state.selectedRows.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				state.selectedRows.slice(0, selectedIndex),
				state.selectedRows.slice(selectedIndex + 1)
			);
		}

		setState((prev) => ({ ...prev, selectedRows: newSelected }));
	};

	/**
	 * Handles the page change event for pagination.
	 * @param {object} event - The event object.
	 * @param {number} newPage - The new page number.
	 */
	const handleChangePage = (event, newPage) => {
		setState((prev) => ({ ...prev, page: newPage }));
	};

	/**
	 * Handles changes to the number of rows per page.
	 * @param {object} event - The event object.
	 */
	const handleChangeRowsPerPage = (event) => {
		const updatedRowsPerPage = parseInt(event.target.value, 10);
		setState((prev) => ({
			...prev,
			rowsPerPage: updatedRowsPerPage,
			page: 0,
			paddingHeight: 0
		}));
	};

	/**
	 * Checks if a row with the given _id is selected.
	 * @param {string} _id - The unique identifier of the row.
	 * @returns {boolean} True if the row is selected, otherwise false.
	 */
	const isSelected = (_id) => state.selectedRows.includes(_id);

	/**
	 * Generates a CSV file from the data and triggers a download.
	 */
	const handleDownload = async () => {
		try {
			// Fetch all records with page set to 0 and rowsPerPage set to totalCount
			const result = await getRecords(baseApiCall, {
				page: 0,
				rowsPerPage: state.totalCount,
				filter: state.filter,
				searchAdditionalQueryParams: state.searchAdditionalQueryParams,
				searchEndpoint: state.searchEndpoint,
				order: order,
				orderBy: orderBy
			});

			if (!result.rows || result.rows.length === 0) {
				return;
			}

			// Create CSV header from headCells labels
			const header = state.headCells.map((cell) => `"${cell.label}"`).join(",");
			// Create CSV rows using headCells id to extract values from each row
			const csvRows = result.rows.map((row) =>
				state.headCells
					.map((cell) => {
						let cellValue = get(row, cell.id, "");
						// Escape quotes by doubling them
						if (typeof cellValue === "string") {
							cellValue = cellValue.replace(/"/g, '""');
						}
						return `"${cellValue}"`;
					})
					.join(",")
			);
			const csvString = [header, ...csvRows].join("\n");

			// Create a Blob from the CSV string and trigger a download
			const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `${props.tableName || "data"}.csv`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (error) {
			console.error("Error during CSV download:", error);
		}
	};

	return (
		<Box sx={{ width: "100%" }}>
			<Paper sx={{ width: "100%", mb: 2 }}>
				<EnhancedTableToolbar
					tableName={props.tableName}
					numSelected={state.selectedRows.length}
					onDelete={() => props.actionDelete(state.selectedRows)}
					onRefresh={() => {
						if (refreshFunction) {
							refreshFunction();
						} else {
							setShouldRefresh((prev) => !prev);
						}
					}}
					onToggleFilter={() => setState((prev) => ({ ...prev, showFilter: !prev.showFilter }))}
					onDownload={handleDownload}
				/>

				{state.showFilter && (
					<FilterSection
						filterList={props.filterList}
						state={state}
						onChangeBulk={(newState) => setState((prev) => ({ ...prev, ...newState }))}
					/>
				)}

				{isTileView ? (
					<TileView
						rows={state.visibleRows}
						headCells={state.headCells}
						handleClick={handleClick}
						handleCheck={handleCheck}
						isSelected={isSelected}
						numSelected={state.selectedRows.length}
						onDelete={props.actionDelete}
					/>
				) : (
					<TableContainer sx={{ maxHeight: "70vh", overflowY: "auto" }}>
						<Table stickyHeader>
							<EnhancedTableHead
								numSelected={state.selectedRows.length}
								order={order}
								orderBy={orderBy}
								onSelectAllClick={handleSelectAllClick}
								onRequestSort={handleRequestSort}
								rowCount={state.rows.length}
								headCells={state.headCells}
							/>
							<TableBody>
								{state.visibleRows.map((row, index) => {
									const isItemSelected = isSelected(row._id);
									const labelId = `enhanced-table-checkbox-${index}`;
									return (
										<TableRow
											hover
											role="checkbox"
											aria-checked={isItemSelected}
											tabIndex={-1}
											key={row._id}
											selected={isItemSelected}
											sx={{ cursor: "pointer" }}
											onClick={(event) => handleClick(event, row)}
										>
											<TableCell padding="checkbox">
												<Checkbox
													color="primary"
													checked={isItemSelected}
													onClick={(event) => {
														event.stopPropagation();
														handleCheck(event, row);
													}}
													inputProps={{
														"aria-labelledby": labelId
													}}
												/>
											</TableCell>
											{state.headCells.map((headCell) =>
												headCell.render ? (
													<TableCell key={headCell.id}>
														{headCell.render({ rowData: row, handleClick, index })}
													</TableCell>
												) : (
													<TableCell
														component="th"
														id={labelId}
														scope="row"
														key={headCell.id}
														padding="none"
													>
														{!isEmpty(get(row, headCell.id))
															? get(row, headCell.id).toString()
															: ""}
													</TableCell>
												)
											)}
										</TableRow>
									);
								})}
								{state.paddingHeight > 0 && (
									<TableRow style={{ height: state.paddingHeight }}>
										<TableCell colSpan={state.headCells.length + 1} />
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				)}

				<Pagination
					page={state.page}
					rowsPerPage={state.rowsPerPage}
					totalCount={state.totalCount}
					onChangePage={handleChangePage}
					onChangeRowsPerPage={handleChangeRowsPerPage}
				/>
			</Paper>
		</Box>
	);
});

EnhancedTable.propTypes = {
	tableName: PropTypes.string.isRequired,
	searchEndpoint: PropTypes.string,
	searchAdditionalQueryParams: PropTypes.object,
	actionRowClick: PropTypes.func,
	actionDelete: PropTypes.func,
	filterList: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			dataType: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
		})
	),
	headCells: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			numeric: PropTypes.bool,
			disablePadding: PropTypes.bool,
			label: PropTypes.string.isRequired,
			render: PropTypes.func
		})
	).isRequired,
	pageSize: PropTypes.number,
	rows: PropTypes.array,
	refreshFunction: PropTypes.func
};

EnhancedTable.defaultProps = {
	searchEndpoint: "",
	searchAdditionalQueryParams: {},
	filterList: [],
	pageSize: DEFAULT_ROWS_PER_PAGE,
	rows: [],
	refreshFunction: null
};

EnhancedTable.displayName = "EnhancedTable";

export default EnhancedTable;
